import { useState } from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import type { DesignBriefInputSource, ToolDesignIntent } from '@shared/types/tool-design';
import { Button } from '@/components/ui';

const SOURCE_LABELS: Record<DesignBriefInputSource, string> = {
  user: 'À chaque lancement',
  profile: 'Profil',
  crm: 'CRM',
  default: 'Règle fixe',
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="border-t border-domino-cardBorder pt-4">
    <h3 className="text-sm font-semibold text-domino-ink">{title}</h3>
    <div className="mt-2 space-y-3">{children}</div>
  </section>
);

/**
 * The Forge's design sheet ("fiche de conception"): what it understood, what
 * goes in and out and why, its choices, its doubts and the success criterion.
 * The user validates this exact sheet before testing; changes go through the
 * chat, and any change to the sheet asks for a new validation.
 */
/** « Modifier » puts the quoted line in the message field; nothing is sent. */
export const EditLine = ({ text, onEdit }: { text: string; onEdit?: (text: string) => void }) => onEdit ? (
  <button
    type="button"
    className="ml-2 text-xs text-domino-aubergine underline underline-offset-4"
    onClick={() => onEdit(text)}
  >
    Modifier
  </button>
) : null;

export const DesignBriefCard = ({ intent, validated, validating, onValidate, onEditLine }: {
  intent: ToolDesignIntent;
  validated: boolean;
  validating: boolean;
  onValidate: () => void;
  onEditLine?: (text: string) => void;
}) => {
  const [open, setOpen] = useState(!validated);
  const decisions = (intent.doubts ?? []).filter((doubt) => doubt.needsDecision).length;

  if (validated && !open) {
    return <div className="flex flex-wrap items-center gap-3 rounded-xl border border-domino-cardBorder bg-domino-white px-4 py-3 text-sm">
      <CircleCheck className="h-4 w-4 text-emerald-700" aria-hidden="true" />
      <span className="text-domino-ink">Conception validée</span>
      <button type="button" className="text-domino-aubergine underline underline-offset-4" onClick={() => setOpen(true)}>
        Voir la fiche
      </button>
    </div>;
  }

  return <article aria-labelledby="design-brief-title" className="space-y-4 rounded-2xl border border-domino-cardBorder bg-domino-white p-5">
    <header className="space-y-1">
      <h2 id="design-brief-title" className="text-base font-semibold text-domino-ink">Fiche de conception</h2>
      <p className="text-sm text-domino-ink">{intent.goal}</p>
      <p className="text-sm text-domino-inkSoft">{intent.value}</p>
    </header>

    {(intent.inputDetails?.length ?? 0) > 0 && <Section title="Entrées">
      {intent.inputDetails!.map((input) => <div key={`${input.source}:${input.label}`} className="text-sm">
        <p className="text-domino-ink">
          <span className="font-medium">{input.label}</span>
          {input.proposal && <> : {input.proposal}</>}
          <span className="ml-2 text-xs text-domino-inkSoft">{SOURCE_LABELS[input.source]}</span>
          <EditLine onEdit={onEditLine} text={`Sur l’entrée « ${input.label}${input.proposal ? ` : ${input.proposal}` : ''} » : `} />
        </p>
        {input.why && <p className="text-domino-inkSoft">{input.why}</p>}
      </div>)}
    </Section>}

    {(intent.outputDetails?.length ?? 0) > 0 && <Section title="Sorties">
      {intent.outputDetails!.map((output) => <div key={output.label} className="text-sm">
        <p className="font-medium text-domino-ink">
          {output.label}
          <EditLine onEdit={onEditLine} text={`Sur la sortie « ${output.label} » : `} />
        </p>
        {output.why && <p className="text-domino-inkSoft">{output.why}</p>}
      </div>)}
    </Section>}

    {(intent.choices?.length ?? 0) > 0 && <Section title="Choix de conception">
      {intent.choices!.map((choice) => <div key={choice.decision} className="text-sm">
        <p className="font-medium text-domino-ink">
          {choice.decision}
          <EditLine onEdit={onEditLine} text={`Sur le choix « ${choice.decision} » : `} />
        </p>
        <p className="text-domino-inkSoft">{choice.why}</p>
        {choice.alternative && <p className="text-xs text-domino-inkSoft">Autre option : {choice.alternative}</p>}
      </div>)}
    </Section>}

    {(intent.doubts?.length ?? 0) > 0 && <Section title="Doutes et points à valider">
      {intent.doubts!.map((doubt) => <div key={doubt.doubt} className="text-sm">
        <p className="flex items-start gap-2 font-medium text-domino-ink">
          {doubt.needsDecision && <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--domino-warning)]" aria-label="À trancher" />}
          {doubt.doubt}
          <EditLine onEdit={onEditLine} text={`Sur le doute « ${doubt.doubt} » : `} />
        </p>
        <p className="text-domino-inkSoft">Conséquence : {doubt.consequence}</p>
        <p className="text-domino-inkSoft">Proposition : {doubt.proposal}</p>
      </div>)}
    </Section>}

    {intent.successCriteria.length > 0 && <Section title="Critère de réussite">
      <ul className="list-disc space-y-1 pl-5 text-sm text-domino-ink">
        {intent.successCriteria.map((criterion) => <li key={criterion}>
          {criterion}
          <EditLine onEdit={onEditLine} text={`Sur le critère de réussite « ${criterion} » : `} />
        </li>)}
      </ul>
    </Section>}

    <footer className="flex flex-wrap items-center gap-3 border-t border-domino-cardBorder pt-4">
      {validated
        ? <Button variant="secondary" onClick={() => setOpen(false)}>Replier la fiche</Button>
        : <Button onClick={onValidate} loading={validating}>Valider la conception</Button>}
      <p className="text-xs text-domino-inkSoft">
        {decisions > 0 ? `${decisions} point${decisions > 1 ? 's' : ''} à trancher. ` : ''}
        Pour changer une ligne, cliquez sur « Modifier » ou dites-le dans la conversation.
      </p>
    </footer>
  </article>;
};
